import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/auth/AuthContext';
import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { LoginPage } from '@/auth/LoginPage';
import { PageLayout } from '@/components/layout/PageLayout';
import { UsersPage } from '@/features/users/ui/UsersPage';
import { UserDetailPage } from '@/features/users/ui/UserDetailPage';
import { NimPage } from '@/features/nim/ui/NimPage';
import { NimMemoryPage } from '@/features/nim/ui/NimMemoryPage';
import { NimGraphPage } from '@/features/nim/ui/NimGraphPage';
import { NimMediaPage } from '@/features/nim/ui/NimMediaPage';
import { NimFirstMessagePage } from '@/features/nim/ui/NimFirstMessagePage';
import { NimCallsPage } from '@/features/nim/ui/NimCallsPage';
import { TransactionsPage } from '@/features/transactions/ui/TransactionsPage';
import { AccountsPage } from '@/features/accounts/ui/AccountsPage';
import { AccountDetailPage } from '@/features/accounts/ui/AccountDetailPage';
import { TransactionDetailPage } from '@/features/transactions/ui/TransactionDetailPage';
import { ConversationsPage } from '@/features/conversations/ui/ConversationsPage';
import { FeedbackPage } from '@/features/feedback/ui/FeedbackPage';
import { ConversationDetailPage } from '@/features/conversations/ui/ConversationDetailPage';
import { FlaggedPage } from '@/features/conversations/ui/FlaggedPage';
import { SystemPage } from '@/features/system/ui/SystemPage';
import { AnnouncementsPage } from '@/features/announcements/ui/AnnouncementsPage';
import { NudgesPage } from '@/features/nudges/ui/NudgesPage';
import { ReferralsPage } from '@/features/referrals/ui/ReferralsPage';
import { FFlagsPage } from '@/features/fflags/ui/FFlagsPage';
import { FFlagsListPage } from '@/features/fflags/ui/FFlagsListPage';
import { FFlagOverridesPage } from '@/features/fflags/ui/FFlagOverridesPage';
import { NewFFlagOverridePage } from '@/features/fflags/ui/NewFFlagOverridePage';
import { RewardsPage } from '@/features/rewards/ui/RewardsPage';
import { PartnersListPage } from '@/features/rewards/ui/PartnersListPage';
import { PartnerDetailPage } from '@/features/rewards/ui/PartnerDetailPage';
import { PartnerFormPage } from '@/features/rewards/ui/PartnerFormPage';
import { BudgetsListPage } from '@/features/rewards/ui/BudgetsListPage';
import { BudgetDetailPage } from '@/features/rewards/ui/BudgetDetailPage';
import { BudgetFormPage } from '@/features/rewards/ui/BudgetFormPage';
import { HowItWorksPage } from '@/features/rewards/ui/HowItWorksPage';
import { OverviewPage } from '@/features/rewards/ui/OverviewPage';
import { PaymentsPage } from '@/features/rewards/ui/PaymentsPage';
import { EventPage } from '@/features/events/ui/EventPage';
import { CheckInPage } from '@/features/events/ui/CheckInPage';
import { IssuancesPage } from '@/features/events/ui/IssuancesPage';
import { CHECK_IN_PATH } from '@/features/events/checkInEvent';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <PageLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/users" replace />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="users/:id" element={<UserDetailPage />} />
              <Route path="transactions" element={<TransactionsPage />} />
              <Route path="transactions/:id" element={<TransactionDetailPage />} />
              <Route path="accounts" element={<AccountsPage />} />
              <Route path="accounts/:id" element={<AccountDetailPage />} />
              <Route path="conversations" element={<ConversationsPage />} />
              <Route path="conversations/:id" element={<ConversationDetailPage />} />
              <Route path="flagged" element={<FlaggedPage />} />
              <Route path="feedback" element={<FeedbackPage />} />
              <Route path="nim" element={<NimPage />}>
                <Route index element={<Navigate to="/nim/memory" replace />} />
                <Route path="memory" element={<NimMemoryPage />} />
                <Route path="graph" element={<NimGraphPage />} />
                <Route path="media" element={<NimMediaPage />} />
                <Route path="first-message" element={<NimFirstMessagePage />} />
                <Route path="calls" element={<NimCallsPage />} />
              </Route>
              <Route path="announcements" element={<AnnouncementsPage />} />
              <Route path="nudges" element={<NudgesPage />} />
              <Route path="referrals" element={<ReferralsPage />} />
              <Route path="fflags" element={<FFlagsPage />}>
                <Route index element={<Navigate to="/fflags/flags" replace />} />
                <Route path="flags" element={<FFlagsListPage />} />
                <Route path="users" element={<FFlagOverridesPage />} />
              </Route>
              <Route path="fflags/users/new" element={<NewFFlagOverridePage />} />
              <Route path="rewards" element={<RewardsPage />}>
                <Route index element={<Navigate to="/rewards/overview" replace />} />
                <Route path="overview" element={<OverviewPage />} />
                <Route path="payments" element={<PaymentsPage />} />
                <Route path="redemptions" element={<Navigate to="/rewards/payments" replace />} />
                <Route path="partners" element={<PartnersListPage />} />
                <Route path="budgets" element={<BudgetsListPage />} />
                <Route path="how-it-works" element={<HowItWorksPage />} />
              </Route>
              <Route path="rewards/partners/new" element={<PartnerFormPage />} />
              <Route path="rewards/partners/:id" element={<PartnerDetailPage />} />
              <Route path="rewards/partners/:id/edit" element={<PartnerFormPage />} />
              <Route path="rewards/budgets/new" element={<BudgetFormPage />} />
              <Route path="rewards/budgets/:id" element={<BudgetDetailPage />} />
              <Route path="rewards/budgets/:id/edit" element={<BudgetFormPage />} />
              <Route path="events" element={<Navigate to={CHECK_IN_PATH} replace />} />
              <Route path="events/:code" element={<EventPage />}>
                <Route index element={<Navigate to="check-in" replace />} />
                <Route path="check-in" element={<CheckInPage />} />
                <Route path="issuances" element={<IssuancesPage />} />
              </Route>
              <Route path="system" element={<SystemPage />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
